/**
 * Where "live" is on an hls.js live playlist, for the live button.
 *
 * hls.js reports a live sync position for any playlist it has loaded, a VOD
 * one included (it is simply near the end), so whether a playlist is live has
 * to come from its details.
 */

/** The parts of hls.js's level details this needs. */
export type LiveLevelDetails =
  | {
    live?: boolean;
    targetduration?: number;
  }
  | null
  | undefined;

/**
 * How many segments behind the position hls.js joins at still counts as live.
 * hls.js plays from that position but it moves on with the clock, so a viewer
 * who spent a segment buffering at start would otherwise lose the lit button
 * with nothing to explain it; one more absorbs a rendition switch, which
 * re-estimates the edge from another level's playlist.
 */
export const LIVE_EDGE_SEGMENTS = 2;

function isLiveSyncPosition(
  position: number | null | undefined,
  details: LiveLevelDetails,
): position is number {
  return details?.live === true && typeof position === 'number' &&
    Number.isFinite(position);
}

/**
 * Where to put the playhead to go live: where hls.js itself would join, a few
 * segments in from the end of the playlist, the nearest position it can play
 * without waiting on the next segment. Null when there is no live playlist.
 */
export function liveSeekTarget(
  syncPosition: number | null | undefined,
  details: LiveLevelDetails,
): number | null {
  return isLiveSyncPosition(syncPosition, details) ? syncPosition : null;
}

/**
 * The earliest playhead position that still counts as live. Not a number when
 * there is no live playlist, which Media Chrome reads as "not live".
 */
export function liveEdgeStart(
  syncPosition: number | null | undefined,
  details: LiveLevelDetails,
): number {
  if (!isLiveSyncPosition(syncPosition, details)) return Number.NaN;
  const segment = details?.targetduration;
  return typeof segment === 'number' && segment > 0
    ? syncPosition - LIVE_EDGE_SEGMENTS * segment
    : Number.NaN;
}
